import React, { ReactNode, useMemo } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';

import { createStyles } from 'utils/createStyle';
import { Tab } from 'features/product/components/ProductDetailScreenTab';

const styles = createStyles({
    tabs: ['flex flex-col w-full'],
    tabList: ['flex', { marginBottom: '10px' }],
    tabsContent: ['text-grey-1 text-sm lg:text-base'],
    tabClasses: [
        '',
        {
            flex: '1 1 33%',
        },
    ],
});

export interface TabContent {
    name: string;
    title: string;
    content?: React.ReactNode;
    className?: string;
}

export interface TabsContainerProps {
    tabItems: TabContent[];
    selectedTabName: string;
    setSelectedTabName: (name: string) => void;
    isLoading?: boolean;
    children?: ReactNode;
    tabsListClasses?: string;
}

export const TabsContainer = ({
    tabItems,
    selectedTabName,
    setSelectedTabName,
    isLoading,
    children,
    tabsListClasses = 'border-b border-solid border-grey-3',
}: TabsContainerProps) => {
    const selectedTab = useMemo(
        () => tabItems.find((ti) => ti.name === selectedTabName),
        [selectedTabName, tabItems]
    );

    if (isLoading) {
        return (
            <div>
                <Skeleton height="10rem" />
            </div>
        );
    }

    return (
        <Tabs.Root
            className={styles.tabs}
            value={selectedTabName}
            onValueChange={setSelectedTabName}
        >
            <Tabs.List className={classNames(styles.tabList, tabsListClasses)}>
                {tabItems.map((tabItem) => (
                    <Tab
                        key={tabItem.name}
                        selected={tabItem.name === selectedTabName}
                        tabId={tabItem.name}
                        title={tabItem.title}
                        className={classNames(styles.tabClasses, tabItem.className)}
                        noUnderline={true}
                        noUpperBorder={true}
                    />
                ))}
            </Tabs.List>
            {/* Render Children or content */}
            <div className={styles.tabsContent}>{children ? children : selectedTab?.content}</div>
        </Tabs.Root>
    );
};
